import { Capacitor } from '@capacitor/core';
import { getSiteUrl } from './siteConfig';

/** Must match iOS CFBundleURLSchemes + Android intent-filter scheme. */
export const NATIVE_APP_URL_SCHEME = 'com.xdetailstudio.bookingapp';

/** True when running inside the Capacitor native shell (iOS/Android). */
export function isNativeApp(): boolean {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
}

/**
 * Build the OAuth return URL for Zernio/Google.
 * - Native: custom scheme so SFSafariViewController hands straight back into the app
 *   with query params intact (pendingDataToken, profileId, …).
 * - Web: return directly into the SPA (same browser session that started connect).
 *   Do NOT use oauth-app-return.html on desktop — that page only deep-links the
 *   native scheme, which Chrome cannot open, so the flow looks "stuck".
 * Never use window.location.origin on native — capacitor://localhost is invalid in Safari.
 */
/**
 * Path an in-progress connect should return to.
 * Nexus Connect stays on Integrations. Anywhere else (Marketing Platforms,
 * for example) comes back to the page that started the connection.
 */
export function oauthReturnPath(): string {
  try {
    const path = window.location.pathname || '/app-store';
    if (path.startsWith('/app-store') || path.startsWith('/integrations')) return '/app-store';
    return path;
  } catch {
    return '/app-store';
  }
}

export function buildGbpOauthReturnUrl(opts?: {
  nextPath?: string;
  tab?: string;
  extraParams?: Record<string, string>;
}): string {
  const params = new URLSearchParams({ gbp_oauth: '1' });
  const next = opts?.nextPath || '/app-store';
  const path = next.startsWith('/') ? next : `/${next}`;
  if (path === '/app-store' || path === '/integrations' || opts?.tab === 'integrations') {
    params.set('tab', 'integrations');
  }
  if (opts?.extraParams) {
    for (const [key, value] of Object.entries(opts.extraParams)) {
      if (value) params.set(key, value);
    }
  }
  // Ayrshare Close button: redirect the opener (Nexus) tab and close the linking tab.
  // Requires the linking page to have been opened with window.open().
  params.set('origin', 'true');

  if (isNativeApp()) {
    // Home (`/`) must stay home. Falling back to app-store would drop a
    // connect that started on the home screen into Integrations.
    if (path === '/') {
      return `${NATIVE_APP_URL_SCHEME}:///?${params.toString()}`;
    }
    const host = path.replace(/^\//, '') || 'app-store';
    return `${NATIVE_APP_URL_SCHEME}://${host}?${params.toString()}`;
  }

  return `${getSiteUrl().replace(/\/$/, '')}${path}?${params.toString()}`;
}

/** OAuth return URL after Ayrshare Facebook, Instagram, or LinkedIn linking. */
export function buildSocialOauthReturnUrl(opts: {
  platform: 'facebook' | 'instagram' | 'linkedin';
  nextPath?: string;
}): string {
  const params = new URLSearchParams({
    social_oauth: '1',
    platform: opts.platform,
  });
  const next = opts.nextPath || '/app-store';
  const path = next.startsWith('/') ? next : `/${next}`;
  if (path === '/app-store' || path === '/integrations') params.set('tab', 'integrations');
  params.set('origin', 'true');

  if (isNativeApp()) {
    if (path === '/') {
      return `${NATIVE_APP_URL_SCHEME}:///?${params.toString()}`;
    }
    const host = path.replace(/^\//, '') || 'app-store';
    return `${NATIVE_APP_URL_SCHEME}://${host}?${params.toString()}`;
  }
  return `${getSiteUrl().replace(/\/$/, '')}${path}?${params.toString()}`;
}

/**
 * Where Gmail OAuth should land after Google redirects through the callback.
 * The callback appends `success=gmail_connected`.
 * Native uses the custom scheme so leaving the app still opens Messages.
 * Never use window.location.origin on native — capacitor://localhost is invalid in Safari.
 */
export function buildGmailOauthReturnUrl(nextPath = '/messages'): string {
  const path = nextPath.startsWith('/') ? nextPath : `/${nextPath}`;
  if (isNativeApp()) {
    if (path === '/') return `${NATIVE_APP_URL_SCHEME}:///`;
    const host = path.replace(/^\//, '');
    return `${NATIVE_APP_URL_SCHEME}://${host}`;
  }
  return `${getSiteUrl().replace(/\/$/, '')}${path}`;
}

/** @deprecated Prefer buildGbpOauthReturnUrl */
export function buildOauthHttpsReturnUrl(pathWithQuery: string): string {
  const path = pathWithQuery.startsWith('/') ? pathWithQuery : `/${pathWithQuery}`;
  return `${getSiteUrl().replace(/\/$/, '')}${path}`;
}

/** Convert an in-app path to a custom-scheme deep link that opens the native app. */
export function buildNativeAppDeepLink(pathWithQuery: string): string {
  const path = pathWithQuery.startsWith('/') ? pathWithQuery.slice(1) : pathWithQuery;
  return `${NATIVE_APP_URL_SCHEME}://${path}`;
}

/**
 * Parse a Capacitor `appUrlOpen` URL into an in-app path (+ search).
 * Supports:
 * - com.xdetailstudio.bookingapp://app-store?tab=integrations&…
 * - com.xdetailstudio.bookingapp:///app-store?…
 * - https://app.nexusbookai.com/oauth-app-return?…
 */
export function nativeOpenUrlToAppPath(url: string): string | null {
  try {
    const trimmed = String(url || '').trim();
    if (!trimmed) return null;

    if (trimmed.startsWith(`${NATIVE_APP_URL_SCHEME}:`)) {
      // customscheme://host/path?query  OR  customscheme:///path?query
      const withoutScheme = trimmed.replace(new RegExp(`^${NATIVE_APP_URL_SCHEME}:/+`), '');
      const withSlash = withoutScheme.startsWith('/') ? withoutScheme : `/${withoutScheme}`;
      // Treat first segment as path (host is often the first path piece for custom schemes)
      return withSlash.split('#')[0] || null;
    }

    if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
      const u = new URL(trimmed);
      if (
        u.pathname === '/oauth-app-return' ||
        u.pathname === '/oauth-app-return.html' ||
        u.pathname.startsWith('/oauth-app-return')
      ) {
        const params = new URLSearchParams(u.search);
        params.delete('next');
        if (!params.has('gbp_oauth')) params.set('gbp_oauth', '1');
        if (!params.has('tab')) params.set('tab', 'integrations');
        const q = params.toString();
        return q ? `/app-store?${q}` : '/app-store?tab=integrations&gbp_oauth=1';
      }
      return `${u.pathname}${u.search}` || null;
    }

    if (trimmed.startsWith('/')) return trimmed.split('#')[0];
    return null;
  } catch {
    return null;
  }
}
